import React, { useEffect, useState } from 'react';
import type { Adapter, SpaceAction } from '../types';
import './contextShellAdapter.scss';

/** Wire action names ContextShell treats as a sphere-scope change (source naming may vary; the delivered action keeps its origin name — resolveWireTargets never renames it). */
const SPHERE_FILTER_ACTIONS = new Set(['filter-by-sphere', 'sphere-selected', 'sphere-selection']);

function sphereIdFromAction(action: SpaceAction): string | undefined {
  const value = action.context?.sphereId;
  return typeof value === 'string' ? value : undefined;
}

/**
 * `ContextShell` catalog entry adapter — `surface.layout: 'primary'` (§7.4, OQ1). Docks one primary
 * region plus an ordered list of secondary Sections, mirroring an Armillary-style fixed-plus-docked
 * layout (SC13). Distinct from `ContextShellOrganism` (context/view state owner, requires a router
 * ancestor) — this is the Space-document layout primitive of the same name; it composes whatever
 * subtree is referenced by `primary`/`sections`, which may itself be a `ContextShellOrganism`-backed
 * composition later, but does not depend on it here.
 *
 * Opts into wire delivery (Phase R2): a `filter-by-sphere`/`sphere-selected` action delivered to
 * this component id overlays `{ sphereId }` onto the data model scope of its primary + section
 * subtrees, via the same `renderChildInScope` mechanism `List` uses for repeater items. An adapter
 * with no handler registered simply never sees a delivery — this one opts in via `ctx.onWireDelivery`.
 */
const ContextShellAdapter: Adapter = ({ componentId, props, ctx }) => {
  const primaryId = typeof props.primary === 'string' ? props.primary : undefined;
  const sectionIds = Array.isArray(props.sections) ? (props.sections as unknown[]).filter((id): id is string => typeof id === 'string') : [];

  const [scopeSphereId, setScopeSphereId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!ctx.onWireDelivery) return undefined;
    return ctx.onWireDelivery((action) => {
      if (!SPHERE_FILTER_ACTIONS.has(action.name)) return;
      setScopeSphereId(sphereIdFromAction(action));
    });
    // `ctx` is rebuilt on every host render; only the subscribe function matters here, and the host
    // keeps one stable closure per component id so this effect runs once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.onWireDelivery]);

  const renderChild = (id: string): React.ReactNode =>
    scopeSphereId !== undefined ? ctx.renderChildInScope(id, { sphereId: scopeSphereId }, '') : ctx.renderChild(id);

  return (
    <div
      className="context-shell-adapter"
      data-component-id={componentId}
      data-surface-layout="primary"
      data-scope-sphere-id={scopeSphereId}
    >
      <div className="context-shell-adapter__primary">{primaryId ? renderChild(primaryId) : null}</div>
      {sectionIds.length > 0 && (
        <div className="context-shell-adapter__sections">
          {sectionIds.map((id) => (
            <div key={id} className="context-shell-adapter__section">
              {renderChild(id)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContextShellAdapter;
